<template>
    <div class="section-history">
        <div class="section-history-header d-flex justify-content-between align-items-center mb-4 flex-wrap gap-md">
            <h2 class="kv-title mb-0 section-history-title">{{ t('balance-usage_history_title') }}</h2>
            <div class="d-flex gap-sm align-items-center section-history-actions">
                <div class="kv-group-by-toggles btn-group btn-group-toggle mr-sm-3 mr-0">
                    <KvTooltip
                        v-for="opt in groupByOptions" 
                        :key="opt.value"
                        :text="opt.disabled ? opt.reason : ''"
                        :disabled="!opt.disabled"
                    >
                        <button 
                            class="btn btn-sm"
                            :class="[{ 'active': currentGroupBy === opt.value }, { 'disabled': opt.disabled }]"
                            :disabled="opt.disabled"
                            @click="handleGroupByChange(opt.value)"
                        >
                            {{ opt.label }}
                        </button>
                    </KvTooltip>
                </div>
                <KvButton 
                    variant="outlined"
                    size="small"
                    prepend-icon="ui-file-02"
                    @click="$emit('open-statement')"
                    class="btn-extrato"
                >
                    {{ t('balance-credits_statement') }}
                </KvButton>
            </div>
        </div>

        <div class="d-flex flex-column">
            <div v-if="loading" class="chart-skeleton-container py-2">
                <div class="skeleton-grid-lines">
                    <div class="grid-line" v-for="l in 3" :key="'line-' + l"></div>
                </div>
                <div class="skeleton-bars-row">
                    <div 
                        v-for="(height, i) in skeletonBars" 
                        :key="'bar-' + i" 
                        class="skeleton-bar-col"
                    >
                        <div 
                            class="skeleton-bar" 
                            :style="{ height: height + '%', animationDelay: (i * 0.05) + 's' }"
                        ></div>
                    </div>
                </div>
                <div class="skeleton-x-axis">
                    <div 
                        v-for="i in 6" 
                        :key="'tick-' + i" 
                        class="skeleton-date-tick"
                        :style="{ animationDelay: (i * 0.15) + 's' }"
                    ></div>
                </div>
            </div>

            <div v-else-if="!hasChartData" class="text-muted text-center py-5">
                <div class="empty-icon-circle mb-3 mx-auto">
                    <Icon icon-class="ui-bar-chart-10" class="text-muted" size="6" />
                </div>
                <p class="mb-0">{{ t('balance-no_automation_found') }}</p>
            </div>

            <div v-else class="chart-scroll-wrapper">
                <div class="w-100 chart-container">
                    <bar-chart
                        :chart-data="groupedChartData"
                        :options="chartOptions"
                        :height="260"
                        ref="chart"
                    />
                </div>
            </div>

            <div class="mt-3 daily-average-info d-flex justify-content-between align-items-center">
                <span v-if="!loading">{{ t('balance-daily_average').replace('{amount}', usageDailyAverage) }}</span>
                <KvSkeleton v-else width="200px" height="14px" />
                
                <KvTooltip :text="t('balance-expand_chart')">
                    <button class="btn-fullscreen btn-fullscreen-small" :disabled="loading" @click="openFullscreen">
                        <Icon icon-class="ui-maximize" size="4" />
                    </button>
                </KvTooltip>
            </div>
        </div>

        <transition name="modal-fade">
            <div v-if="isFullscreen" class="chart-fullscreen-overlay" @click.self="closeFullscreen">
                <div class="chart-fullscreen-panel">
                    <div class="d-flex justify-content-between align-items-center mb-4">
                        <h3 class="fullscreen-title mb-0">{{ t('balance-usage_history_title') }}</h3>
                        <button class="btn-fullscreen btn-fullscreen-close" @click="closeFullscreen">
                            <Icon icon-class="ui-x-close" size="5" />
                        </button>
                    </div>
                    <div class="fullscreen-chart-container">
                        <div class="text-muted text-center py-5" v-if="!hasChartData">
                            <div class="empty-icon-circle mb-3 mx-auto">
                                <Icon icon-class="ui-bar-chart-10" class="text-muted" size="6" />
                            </div>
                            <p class="mb-0">{{ t('balance-no_automation_found') }}</p>
                        </div>
                        <bar-chart
                            v-else
                            :chart-data="groupedChartData"
                            :options="chartOptions"
                            :height="600"
                            ref="chartFullscreen"
                            style="width:100%; height:100%;"
                        />
                    </div>
                    <div class="mt-3 daily-average-info">
                        {{ t('balance-daily_average').replace('{amount}', usageDailyAverage) }}
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import moment from "moment";
import BarChart from "../../components/charts/barChart";
import KvButton from "../../components/form/kv-button.vue";
import KvTooltip from "../../components/ui/kv-tooltip.vue";
import KvSkeleton from "../../components/ui/kv-skeleton.vue";
import Icon from "../../components/icon/icon.vue";
import FormatMixin from "../mixins/format-mixin.js";
import { DYNAMIC_COLORS_POOL } from "../utils/format.js";
import { groupByBlockReason } from "../utils/group-by.js";
import { escapeHtml, getTooltipEl, hideTooltip, positionTooltip, removeTooltip } from "../../components/charts/html-tooltip.js";

const TOOLTIP_ID = 'chartjs-tooltip-usage';
const TOOLTIP_MAX_ITEMS = 20;

export default {
    name: "ActionsUsageHistory",
    mixins: [FormatMixin],
    components: {
        BarChart,
        KvButton,
        KvTooltip,
        KvSkeleton,
        Icon,
    },
    props: {
        chartData: { type: Object, default: () => ({ labels: [], datasets: [], rawLabels: [] }) },
        usageDailyAverage: { type: String, default: "0" },
        filters: { type: Object, default: () => ({}) },
        loading: { type: Boolean, default: false }
    },
    data() {
        const self = this;
        return {
            skeletonBars: [
                28, 48, 62, 35, 75, 52, 88, 42, 65, 80, 55, 92, 38, 70,
                85, 48, 62, 78, 95, 52, 68, 82, 40, 74, 88, 58, 45, 68
            ],
            isFullscreen: false,
            colorsPool: DYNAMIC_COLORS_POOL,
            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                legend: { display: false },
                scales: {
                    yAxes: [{
                        stacked: true,
                        ticks: { 
                            beginAtZero: true,
                            maxTicksLimit: 6
                        },
                        scaleLabel: {
                            display: true,
                            labelString: this.t('balance-credits_consumption')
                        }
                    }],
                    xAxes: [{
                        stacked: true,
                        gridLines: { display: false }
                    }]
                },
                hover: {
                    mode: 'index',
                    intersect: false
                },
                onHover: function (event) {
                    self.handleChartHover(this, event);
                },
                tooltips: {
                    enabled: false,
                    mode: 'index',
                    intersect: false,
                    position: 'nearest',
                    custom: function (tooltipModel) {
                        self.tooltipState.chart = this._chart;
                        self.tooltipState.model = tooltipModel;
                        self.renderUsageTooltip(this._chart, tooltipModel);
                    }
                },
                plugins: {
                    barTextPlugin: false 
                }
            }
        };
    },
    created() {
        this.tooltipState = { chart: null, model: null, hoveredLabel: null };
    },
    computed: {
        hasChartData() {
            if (!this.chartData || !this.chartData.datasets || !this.chartData.datasets.length) {
                return false;
            }
            return this.chartData.datasets.some(ds => {
                return ds.data && ds.data.some(val => parseFloat(val) > 0);
            });
        },
        groupByOptions() {
            const days = this.periodInDays;
            return [
                { label: this.t('balance-group_daily'), value: 'day' },
                { label: this.t('balance-group_weekly'), value: 'week' },
                { label: this.t('balance-group_monthly'), value: 'month' },
                { label: this.t('balance-group_quarterly'), value: 'quarter' },
            ].map(opt => {
                const reason = groupByBlockReason(opt.value, days);
                return { ...opt, disabled: !!reason, reason: reason ? this.t(reason) : '' };
            });
        },
        currentGroupBy() {
            return this.chartData && this.chartData.group_by ? this.chartData.group_by : (this.filters.group_by || 'day');
        },
        periodInDays() {
            if (!this.filters.period || !this.filters.period.start || !this.filters.period.end) return 0;
            const start = moment(this.filters.period.start);
            const end = moment(this.filters.period.end);
            return end.diff(start, 'days') + 1;
        },
        groupedChartData() {
            if (!this.chartData || !this.chartData.rawLabels || !this.chartData.rawLabels.length) {
                return this.chartData;
            }

            const groupBy  = this.currentGroupBy;
            const datasets = this.chartData.datasets;
            const labels = this.chartData.rawLabels.map(label => this.formatLabel(label, groupBy));
            const numDays = labels.length;

            const automations = datasets.map(ds => ({
                label: this.$_(ds.label),
                data: ds.data,
                total: ds.data.reduce((acc, val) => acc + (parseFloat(val) || 0), 0)
            }));

            const sortedByTotal = [...automations].sort((a, b) => b.total - a.total);
            const colorMap = {};
            sortedByTotal.forEach((auto, i) => {
                colorMap[auto.label] = this.colorsPool[i % this.colorsPool.length];
            });

            const maxRanks = automations.length;
            const rankedDatasets = [];

            for (let rank = 0; rank < maxRanks; rank++) {
                rankedDatasets.push({
                    label: 'Rank ' + (rank + 1),
                    data: new Array(numDays).fill(0),
                    backgroundColor: new Array(numDays).fill('transparent'),
                    hoverBackgroundColor: new Array(numDays).fill('transparent'),
                    hoverBorderColor: '#FFFFFF',
                    hoverBorderWidth: 2,
                    stack: 'Stack 0',
                    _automationLabels: new Array(numDays).fill(''),
                });
            }

            for (let dayIdx = 0; dayIdx < numDays; dayIdx++) {
                const dayValues = automations.map(auto => ({
                    label: auto.label,
                    value: parseFloat(auto.data[dayIdx]) || 0,
                    color: colorMap[auto.label]
                }));

                dayValues.sort((a, b) => b.value - a.value);

                dayValues.forEach((item, rank) => {
                    rankedDatasets[rank].data[dayIdx] = item.value;
                    rankedDatasets[rank].backgroundColor[dayIdx] = item.color;
                    rankedDatasets[rank].hoverBackgroundColor[dayIdx] = item.color;
                    rankedDatasets[rank]._automationLabels[dayIdx] = item.label;
                });
            }

            return { labels, datasets: rankedDatasets };
        },
    },
    methods: {
        handleChartHover(chart, event) {
            const element = chart.getElementAtEvent(event)[0];
            let hoveredLabel = null;
            if (element) {
                const ds = chart.data.datasets[element._datasetIndex];
                hoveredLabel = ds._automationLabels ? ds._automationLabels[element._index] : ds.label;
            }
            if (hoveredLabel === this.tooltipState.hoveredLabel) return;
            this.tooltipState.hoveredLabel = hoveredLabel;
            if (this.tooltipState.chart === chart && this.tooltipState.model && this.tooltipState.model.opacity !== 0) {
                this.renderUsageTooltip(chart, this.tooltipState.model);
            }
        },
        buildTooltipRows(items, hoveredLabel) {
            if (items.length <= TOOLTIP_MAX_ITEMS + 1) {
                return items;
            }
            const rows = items.slice(0, TOOLTIP_MAX_ITEMS);
            let rest = items.slice(TOOLTIP_MAX_ITEMS);
            const hoveredIdx = rest.findIndex(item => item.label === hoveredLabel);
            if (hoveredIdx !== -1) {
                rows.push(rest[hoveredIdx]);
                rest = rest.filter((_, i) => i !== hoveredIdx);
            }
            rows.push({
                label: this.t('balance-tooltip_others').replace('{count}', rest.length),
                value: rest.reduce((sum, item) => sum + item.value, 0),
                color: '#9CA3AF',
                isOthers: true
            });
            return rows;
        },
        renderUsageTooltip(chart, tooltipModel) {
            const tooltipEl = getTooltipEl(TOOLTIP_ID, {
                borderRadius: '8px',
                padding: '12px 14px',
                boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.12)',
                minWidth: '220px',
                maxWidth: 'min(480px, 90vw)',
            });

            if (tooltipModel.opacity === 0) {
                hideTooltip(tooltipEl);
                return;
            }

            if (tooltipModel.body) {
                tooltipEl.innerHTML = this.buildTooltipHtml(chart, tooltipModel);
            }

            positionTooltip(tooltipEl, chart, tooltipModel);
        },
        buildTooltipHtml(chart, tooltipModel) {
            const hoveredLabel = this.tooltipState.hoveredLabel;

            const items = (tooltipModel.dataPoints || []).map(dp => {
                const ds = chart.data.datasets[dp.datasetIndex];
                const label = ds._automationLabels ? ds._automationLabels[dp.index] : ds.label;
                const value = parseFloat(dp.yLabel || dp.value || 0);
                const color = Array.isArray(ds.backgroundColor) ? ds.backgroundColor[dp.index] : ds.backgroundColor;
                return { label, value, color };
            });

            const filtered = items
                .filter(item => item.value > 0)
                .sort((a, b) => b.value - a.value);

            const totalDay = filtered.reduce((sum, item) => sum + item.value, 0);
            const dateTitle = escapeHtml((tooltipModel.title || []).join(' '));

            let html = '<div style="display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid #E5E7EB;">';
            html += `<span style="font-weight: 600; color: #00542E; font-size: 13px;">${dateTitle}</span>`;
            html += `<span style="font-weight: 600; color: #111827; font-size: 13px;">${this.formatNumber(totalDay, 2)} ${this.t('balance-credits')}</span>`;
            html += '</div>';

            if (filtered.length === 0) {
                return html + `<div style="color: #6B7280; font-size: 12px; padding: 4px 0;">${this.t('balance-no_consumption_for_period')}</div>`;
            }

            html += '<div style="display: flex; flex-direction: column; gap: 2px;">';
            this.buildTooltipRows(filtered, hoveredLabel).forEach(item => {
                const isHovered = !item.isOthers && item.label === hoveredLabel;
                const percent = totalDay > 0 ? ((item.value / totalDay) * 100).toFixed(1) : '0.0';
                const rowStyle = isHovered ? 'background: #F3F4F6; font-weight: 600;' : '';
                const labelColor = isHovered ? '#111827' : (item.isOthers ? '#6B7280' : '#374151');
                const labelStyle = item.isOthers ? 'font-style: italic;' : '';
                const valueWeight = isHovered ? 600 : 500;
                const dot = `<span style="display: inline-block; width: 8px; height: 8px; margin-right: 8px; border-radius: 50%; background: ${escapeHtml(item.color)}; flex-shrink: 0;"></span>`;
                html += `<div style="display: flex; align-items: center; justify-content: space-between; gap: 14px; font-size: 12px; padding: 2px 6px; margin: 0 -6px; border-radius: 4px; ${rowStyle}">` +
                            `<span style="display: flex; align-items: center; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 350px; color: ${labelColor}; ${labelStyle}">${dot}${escapeHtml(item.label)}</span>` +
                            `<span style="font-weight: ${valueWeight}; color: #111827; white-space: nowrap; flex-shrink: 0;">${this.formatNumber(item.value, 2)} <span style="color: #6B7280; font-weight: 400;">(${percent}%)</span></span>` +
                        '</div>';
            });
            return html + '</div>';
        },
        handleGroupByChange(val) {
            if (val === this.currentGroupBy) return;
            this.$emit('group-by-changed', val);
        },
        openFullscreen() {
            this.isFullscreen = true;
            document.addEventListener('keydown', this._handleEsc);
        },
        closeFullscreen() {
            this.isFullscreen = false;
            document.removeEventListener('keydown', this._handleEsc);
        },
        _handleEsc(e) {
            if (e.key === 'Escape') this.closeFullscreen();
        },
        formatLabel(dateStr, groupBy) {
            if (groupBy === 'month') {
                return moment(dateStr, 'YYYY-MM').format('MMM/YY');
            }
            if (groupBy === 'quarter') {
                const [year, q] = dateStr.split('-');
                return q + '/' + year.substring(2);
            }
            const d = moment(dateStr, 'YYYY-MM-DD');
            if (groupBy === 'week') {
                return d.format('DD/MM') + ' a ' + d.clone().add(6, 'days').format('DD/MM');
            }
            return d.format('DD/MM');
        }
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this._handleEsc);
        removeTooltip(TOOLTIP_ID);
    }
};
</script>

<style scoped lang="scss">
.gap-md { gap: 16px; }

.section-history-title {
    font-size: 20px;
    
    @media (max-width: 576px) {
        width: 100%;
        text-align: center;
    }
}

.daily-average-info {
    font-size: 15px;
    color: #4B5563;

    @media (max-width: 576px) {
        text-align: center;
    }
}

.text-dark-emphasis {
    color: #111827;
}

.chart-scroll-wrapper {
    min-height: 260px;
    width: 100%;

    @media (max-width: 576px) {
        min-height: 200px;
    }
}

.chart-container {
    height: 260px;
    width: 100%;
    position: relative;

    @media (max-width: 576px) {
        height: 200px;
    }
}

.kv-group-by-toggles {
    display: inline-flex;
    align-items: center;
    background: #F3F4F6;
    padding: 3px;
    border-radius: 8px;
    border: 1px solid #E5E7EB;
    
    @media (max-width: 576px) {
        width: 100%;
        justify-content: center;
        flex-wrap: wrap;
    }

    .btn {
        border: none;
        border-radius: 6px !important;
        background: transparent;
        color: #6B7280;
        font-weight: 500;
        font-size: 12px;
        padding: 5px 14px;
        transition: all 0.15s ease;
        position: relative;
        box-shadow: none !important;
        outline: none !important;

        @media (max-width: 576px) {
            flex: 1 1 auto;
            min-width: 70px;
            padding: 5px 8px;
        }
        
        &:hover:not(.disabled):not(.active) {
            color: #111827;
            background: rgba(0, 0, 0, 0.04);
        }
        
        &.active {
            background: #FFFFFF !important;
            color: #15803D !important;
            font-weight: 600;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04) !important;
        }

        &.disabled {
            opacity: 0.4;
            cursor: not-allowed;
            background: transparent !important;
            color: #9CA3AF !important;
        }
    }
}

.btn-extrato {
    border-color: #E5E7EB !important;
    color: #4B5563 !important;
    background-color: #FFFFFF !important;
    font-weight: 500 !important;
    transition: all 0.15s ease;

    @media (max-width: 576px) {
        width: 100%;
        justify-content: center;
    }
    
    &:hover {
        background-color: #F9FAFB !important;
        border-color: #D1D5DB !important;
        color: #111827 !important;
    }
}

.section-history-header {
    @media (max-width: 576px) {
        flex-direction: column !important;
        align-items: stretch !important;
        gap: 12px;

        .section-history-title {
            text-align: center;
        }
    }
}

.section-history-actions {
    @media (max-width: 576px) {
        flex-direction: column !important;
        width: 100%;
        gap: 8px;
    }
}

.btn-fullscreen {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    background: #FFFFFF;
    color: #9CA3AF;
    cursor: pointer;
    transition: all 0.2s ease;
    padding: 0;

    &:hover {
        background: #F1FCF3;
        border-color: #2AAA4C;
        color: #2AAA4C;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }

    &.btn-fullscreen-small {
        width: 28px;
        height: 28px;
        border-radius: 6px;
    }

    &.btn-fullscreen-close {
        width: 36px;
        height: 36px;
        border: none;
        background: transparent;
        border-radius: 0;
        color: #9CA3AF;

        &:hover {
            background: transparent;
            border-color: transparent;
            color: #9CA3AF;
            box-shadow: none;
        }
    }
}

.chart-fullscreen-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.75);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;

    @media (max-width: 576px) {
        padding: 8px;
    }
}

.chart-fullscreen-panel {
    background: #FFFFFF;
    border-radius: 20px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    padding: 28px 32px;
    width: 95vw;
    max-width: 1400px;
    height: 90vh;
    max-height: 90vh;
    overflow: auto;
    display: flex;
    flex-direction: column;
    position: relative;
    z-index: 10000;

    @media (max-width: 576px) {
        padding: 16px 12px;
        border-radius: 14px;
        width: 100%;
        height: 96vh;
        max-height: 96vh;
    }
}

.fullscreen-title {
    font-size: 20px;
    font-weight: 500;
    color: #111827;
}

.fullscreen-chart-container {
    flex: 1;
    min-height: 0;
    width: 100%;
    position: relative;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.2s ease;
}
.modal-fade-enter,
.modal-fade-leave-to {
    opacity: 0;
}

.empty-icon-circle {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background-color: #F8FAFC;
    border: 1px solid #E2E8F0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.chart-skeleton-container {
    height: 260px;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    position: relative;
    padding-bottom: 4px;

    @media (max-width: 576px) {
        height: 200px;
    }

    .skeleton-grid-lines {
        position: absolute;
        top: 20px;
        left: 0;
        right: 0;
        bottom: 40px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        pointer-events: none;

        .grid-line {
            width: 100%;
            height: 1px;
            border-bottom: 1px dashed #F1F5F9;
        }
    }

    .skeleton-bars-row {
        height: 200px;
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 6px;
        padding: 0 10px;
        border-bottom: 2px solid #E2E8F0;
        position: relative;
        z-index: 1;

        @media (max-width: 576px) {
            height: 150px;
            gap: 3px;
        }

        .skeleton-bar-col {
            flex: 1;
            height: 100%;
            display: flex;
            align-items: flex-end;
            justify-content: center;
        }

        .skeleton-bar {
            width: 100%;
            max-width: 18px;
            min-width: 4px;
            border-radius: 4px 4px 0 0;
            background: linear-gradient(90deg, #F1F5F9 25%, #E2E8F0 50%, #F1F5F9 75%);
            background-size: 200% 100%;
            animation: kv-shimmer-anim 1.5s infinite;
        }
    }

    .skeleton-x-axis {
        display: flex;
        justify-content: space-between;
        padding: 10px 10px 0;

        .skeleton-date-tick {
            width: 38px;
            height: 10px;
            border-radius: 3px;
            background: linear-gradient(90deg, #F1F5F9 25%, #E2E8F0 50%, #F1F5F9 75%);
            background-size: 200% 100%;
            animation: kv-shimmer-anim 1.5s infinite;
        }
    }
}

@keyframes kv-shimmer-anim {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
}

</style>
